import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getTeamOfRound,
  getTotrLeaderboard,
  getTotrRound,
  submitTotrPrediction,
} from '@/lib/data/provider';

/** Best XI of the latest finished gameweek (read-only showcase + widgets). */
export function useTeamOfRound() {
  return useQuery({
    queryKey: ['team-of-round'],
    queryFn: getTeamOfRound,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured,
  });
}

/** The active prediction round: predict window when open, reveal once scored. */
export function useTotrRound() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['totr', 'round', userId ?? 'anon'],
    queryFn: () => getTotrRound(),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured,
  });
}

export function useSubmitTotrPrediction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { gameweekId: string; playerIds: string[]; captainId: string }) =>
      submitTotrPrediction(input.gameweekId, input.playerIds, input.captainId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['totr'] });
      queryClient.invalidateQueries({ queryKey: ['rewards'] });
    },
  });
}

export function useTotrLeaderboard(enabled = true) {
  return useQuery({
    queryKey: ['totr', 'leaderboard'],
    queryFn: () => getTotrLeaderboard(),
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && enabled,
  });
}
